import React, { useEffect, useState } from "react";
import moment, { Moment } from "moment";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, IconButton, Typography } from "@mui/material";
import "./index.css";

interface IProps {
  selectedDateTime: { date?: Moment; time: string };
  handlePickDate: (date: Moment) => void;
  setDate: (agr: { startDate: number; endDate: number }) => void;
  date: { startDate: number; endDate: number };
  maxAdvanceDays: number;
  isLoading: boolean;
  availableTime: {
    [key: string]: Array<string>;
  };
}

const OneWeekCalendar: React.FC<IProps> = ({
  selectedDateTime,
  handlePickDate,
  setDate,
  date,
  maxAdvanceDays,
  isLoading,
  availableTime,
}) => {
  const [weekStart, setWeekStart] = useState<Moment>(moment().startOf("week"));
  const today = moment();

  const previous = (): void => {
    setWeekStart((prevWeekStart) => prevWeekStart.clone().subtract(1, "week"));
  };

  const next = (): void => {
    setWeekStart((prevWeekStart) => prevWeekStart.clone().add(1, "week"));
  };

  const select = (day: { date: Moment }): void => {
    handlePickDate(day.date);
  };

  const renderDayNames = (): JSX.Element => (
    <div className="row day-names">
      {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
        <Typography className="day" key={day}>
          {day}
        </Typography>
      ))}
    </div>
  );

  const renderDays = (): JSX.Element => {
    let days: JSX.Element[] = [];
    let date: Moment = weekStart.clone(); // Clone the weekStart here
    for (var i = 0; i < 7; i++) {
      // Clone the date object before manipulating it
      let currentDay = date.clone().add(i, "day"); // Add 'i' days only once, without modifying 'date'
      let day = {
        number: currentDay.date(),
        date: currentDay,
        isDisabled:
          moment(currentDay).unix() >= moment().startOf("d").unix() &&
          Object.keys(availableTime).some(
            (date) => date === moment(currentDay).format("YYYY-MM-DD")
          )
            ? false
            : true,
      };

      days.push(
        <Box
          key={currentDay.toString()}
          className={`day${
            selectedDateTime.date &&
            moment(day.date).date() === moment(selectedDateTime.date).date()
              ? " selected"
              : ""
          }`}
          sx={{
            pointerEvents: day.isDisabled ? "none" : "auto",
            color: day.isDisabled ? "#ccc" : "#000",
          }}
          onClick={() => select(day)}
        >
          <Box>
            <Typography>{day.number}</Typography>
          </Box>
        </Box>
      );
    }

    return <div className="row week">{days}</div>;
  };

  const renderWeekLabel = (): JSX.Element => (
    <Typography className="week-label" fontWeight={600} fontSize={15}>
      {weekStart.format("MMMM DD")} -&nbsp;
      {weekStart.clone().endOf("week").format("MMMM DD YYYY")}
    </Typography>
  );

  useEffect(() => {
    setDate({
      startDate: weekStart.clone().startOf("week").unix(),
      endDate: weekStart.clone().endOf("week").unix(),
    });
  }, [JSON.stringify(weekStart)]);

  return (
    <section
      className="calendar"
      style={{ pointerEvents: isLoading ? "none" : "auto" }}
    >
      <header className="header">
        <div className="month-display row">
          <IconButton
            onClick={previous}
            sx={{
              // Disable pointer events if it's the current week
              pointerEvents:
                weekStart.isSameOrBefore(today, "week") || isLoading
                  ? "none"
                  : "auto",
            }}
          >
            <ArrowBackIosNewIcon
              sx={{
                width: "15px",
                height: "15px",
                color:
                  weekStart.isSameOrBefore(today, "week") || isLoading
                    ? "#ccc"
                    : "inherit", // Change color if it's the current week
              }}
              className="arrow"
            />
          </IconButton>
          {renderWeekLabel()}
          <IconButton
            onClick={next}
            disabled={moment
              .unix(date.endDate)
              .isSameOrAfter(moment().add(maxAdvanceDays, "days"))}
          >
            <ArrowForwardIosIcon
              sx={{ width: "15px", height: "15px" }}
              className="arrow"
            />
          </IconButton>
        </div>
        {renderDayNames()}
      </header>
      {renderDays()}
    </section>
  );
};

export default OneWeekCalendar;
